/**
 * Push Notification Subscription Manager
 * Handles subscribing users to Web Push and storing subscriptions in backend
 */

import { bridgeApi } from '@/lib/bridgeApi';

// VAPID public key for push subscription
const VAPID_PUBLIC_KEY = 'BIGuWObz9nSykon1Tsxy0T-3HqCbKUOKAWMMRsXw5UqeBKdjzQ9rbUKxdJBIAbcEYPacz07cUxs9qw4rKkJ2tzI';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Check if push notifications are supported and permission status
 */
export function getPushStatus(): { supported: boolean; permission: string } {
  const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const permission = 'Notification' in window ? Notification.permission : 'unsupported';
  return { supported, permission };
}

/**
 * Request push notification permission and subscribe
 */
export async function subscribeToPush(userId: number | string, dashboardType: string = 'member'): Promise<boolean> {
  const { supported } = getPushStatus();
  if (!supported) {
    console.warn('[Push] Push notifications not supported');
    return false;
  }

  try {
    // Request permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn('[Push] Permission denied');
      return false;
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;

    // Check for existing subscription
    let subscription = await registration.pushManager.getSubscription();

    // Subscribe if not already subscribed
    if (!subscription) {
      const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as BufferSource,
      });
    }

    // Send subscription to backend
    const subJson = subscription.toJSON();
    await bridgeApi.post('save_push_subscription', {
      user_id: userId,
      dashboard_type: dashboardType,
      endpoint: subJson.endpoint,
      p256dh: subJson.keys?.p256dh || '',
      auth: subJson.keys?.auth || '',
      expiration_time: subJson.expirationTime || null,
    });

    return true;
  } catch (error) {
    console.error('[Push] Subscription failed:', error);
    return false;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(userId: number | string): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
      await bridgeApi.post('remove_push_subscription', {
        user_id: userId,
        endpoint: subscription.endpoint,
      });
    }
    return true;
  } catch (error) {
    console.error('[Push] Unsubscribe failed:', error);
    return false;
  }
}

/**
 * Check if currently subscribed to push
 */
export async function isPushSubscribed(): Promise<boolean> {
  try {
    if (!('serviceWorker' in navigator)) return false;
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}
